import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import type { FavoriteItem } from '../types/favorites';
import type { SupportedLanguages } from '../i18n';
import { logger } from '../lib/logger';
import { hotelConfig } from '../config/hotel.config';
import { slugify } from './slugify';
import { formatPhoneNumber } from './formatters';
import { formatTravelTime } from './formatDuration';

function stripHtml(html: string): string {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

function hexToRgb(hex: string): [number, number, number] {
  const c = (hex || '#000000').replace('#', '');
  if (!/^[0-9A-Fa-f]{6}$/.test(c)) return [0, 0, 0];
  return [
    parseInt(c.substring(0, 2), 16),
    parseInt(c.substring(2, 4), 16),
    parseInt(c.substring(4, 6), 16),
  ];
}

function formatDateSafe(lang: string, options: Intl.DateTimeFormatOptions, date?: Date): string {
  try {
    return (date || new Date()).toLocaleDateString(lang, options);
  } catch {
    return (date || new Date()).toLocaleDateString('en', options);
  }
}

export async function generateFavoritesPDF(
  favorites: FavoriteItem[],
  config: any,
  translations: Record<string, Record<string, string>>,
  language: SupportedLanguages
): Promise<void> {
  try {
    const useFallback = ['ja', 'zh', 'ru'].includes(language);
    const pdfLang = useFallback ? 'en' : language;

    logger.debug('Starting PDF generation', {
      category: 'PDF',
      data: { language, useFallback, pdfLang, favoritesCount: favorites.length }
    });

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    const accentColor = config['major-color'] || '#2563EB';
    const darkColor = config['dark-color'] || '#1a1a2e';
    const mediumColor = config['medium-color'] || '#6b7280';
    const [acR, acG, acB] = hexToRgb(accentColor);
    const [dkR, dkG, dkB] = hexToRgb(darkColor);
    const [mdR, mdG, mdB] = hexToRgb(mediumColor);

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;

    const t = (key: string): string => {
      const tr = translations[key];
      if (!tr) return key;
      return (useFallback ? tr['en'] : tr[language]) || tr['fr'] || key;
    };

    const loadImage = (url: string): Promise<HTMLImageElement> =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('load failed'));
        img.src = url;
      });

    drawCoverHeader(doc, config, acR, acG, acB, t, pdfLang, favorites.length);
    let yPos = 52;

    const addPage = () => {
      doc.addPage();
      doc.setFillColor(acR, acG, acB);
      doc.rect(0, 0, pageWidth, 18, 'F');
      doc.setFillColor(
        Math.max(0, acR - 20),
        Math.max(0, acG - 20),
        Math.max(0, acB - 20)
      );
      doc.rect(0, 15, pageWidth, 3, 'F');
      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.text(config.name, margin, 12);
      doc.setFontSize(8);
      doc.text(t('favorites'), pageWidth - margin, 12, { align: 'right' });
      yPos = 26;
    };

    const checkPage = (h: number) => {
      if (yPos + h > pageHeight - 20) {
        addPage();
      }
    };

    const sortedFavorites = [...favorites].sort((a, b) => {
      const aD = (a.sourceType === 'event' || a.sourceType === 'event-api') && a.data.date;
      const bD = (b.sourceType === 'event' || b.sourceType === 'event-api') && b.data.date;
      if (aD && bD) return new Date(a.data.date).getTime() - new Date(b.data.date).getTime();
      if (aD) return -1;
      if (bD) return 1;
      return a.data.name.localeCompare(b.data.name);
    });

    for (const favorite of sortedFavorites) {
      const cardH = await renderFavoriteCard(
        doc, favorite, config, yPos, margin, contentWidth, pageWidth,
        checkPage, loadImage, t, pdfLang, language, useFallback,
        acR, acG, acB, dkR, dkG, dkB, mdR, mdG, mdB, darkColor
      );
      yPos += cardH + 6;
    }

    addPageFooters(doc, config, t, pdfLang, margin, pageWidth, pageHeight, mdR, mdG, mdB);

    const ts = new Date().toISOString().replace(/[^0-9]/g, '').slice(0, 14);
    const fileName = `${config.name.toLowerCase().replace(/\s+/g, '-')}-favoris-${ts}.pdf`;
    doc.save(fileName);

    logger.debug('PDF generated successfully', {
      category: 'PDF',
      data: { itemCount: favorites.length, fileName, pages: doc.getNumberOfPages(), language }
    });
  } catch (error) {
    logger.error('Error generating PDF', { category: 'PDF', data: { error } });
    throw error;
  }
}

function drawCoverHeader(
  doc: jsPDF,
  config: any,
  acR: number, acG: number, acB: number,
  t: (k: string) => string,
  pdfLang: string,
  count: number
) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;

  doc.setFillColor(acR, acG, acB);
  doc.rect(0, 0, pageWidth, 44, 'F');

  doc.setFillColor(
    Math.max(0, acR - 20),
    Math.max(0, acG - 20),
    Math.max(0, acB - 20)
  );
  doc.rect(0, 41, pageWidth, 3, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.text(config.name, margin, 16);

  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(0.4);
  doc.line(margin, 20, margin + 40, 20);

  doc.setFontSize(13);
  doc.text(`${t('favorites')} (${count})`, margin, 29);

  doc.setFontSize(8);
  const dateStr = formatDateSafe(pdfLang, {
    year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
  doc.text(dateStr, margin, 37);
}

function addPageFooters(
  doc: jsPDF,
  config: any,
  t: (k: string) => string,
  pdfLang: string,
  margin: number,
  pageWidth: number,
  pageHeight: number,
  mdR: number, mdG: number, mdB: number
) {
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    doc.setDrawColor(220, 222, 228);
    doc.setLineWidth(0.3);
    doc.line(margin, pageHeight - 14, pageWidth - margin, pageHeight - 14);

    doc.setFontSize(7);
    doc.setTextColor(mdR, mdG, mdB);

    const footerLeft = `${config.name}${config.fulladress ? ' - ' + config.fulladress : ''}`;
    const maxW = (pageWidth - 2 * margin) * 0.5;
    const truncated = doc.splitTextToSize(footerLeft, maxW);
    doc.text(truncated[0], margin, pageHeight - 9);

    const genText = `${t('generated_on') || 'Generated'} ${formatDateSafe(pdfLang, {
      year: 'numeric', month: 'short', day: 'numeric'
    })}`;
    doc.text(genText, pageWidth / 2, pageHeight - 9, { align: 'center' });

    doc.text(`${i} / ${totalPages}`, pageWidth - margin, pageHeight - 9, { align: 'right' });
  }
}

async function renderFavoriteCard(
  doc: jsPDF,
  favorite: FavoriteItem,
  config: any,
  yPos: number,
  margin: number,
  contentWidth: number,
  pageWidth: number,
  checkPage: (h: number) => void,
  loadImage: (url: string) => Promise<HTMLImageElement>,
  t: (k: string) => string,
  pdfLang: string,
  language: SupportedLanguages,
  useFallback: boolean,
  acR: number, acG: number, acB: number,
  dkR: number, dkG: number, dkB: number,
  mdR: number, mdG: number, mdB: number,
  darkColor: string
): Promise<number> {
  const item = favorite.data;
  const imgUrl = item.image || favorite.metadata.sourceSection.replace_img?.[0]?.url;
  const hasImg = !!imgUrl;

  const imgW = 55;
  const imgH = 36;
  const qrSize = 18;
  const accentBarW = 2.5;
  const cardPad = 5;
  const cardRadius = 3;

  let desc = useFallback
    ? (favorite.translations?.descriptions?.['en'] || item.descriptions?.['en'] || item.descriptions?.['fr'] || item.description)
    : (favorite.translations?.descriptions?.[language] || item.descriptions?.[language] || item.description);
  desc = stripHtml(desc);

  const leftZoneX = margin + accentBarW + cardPad;
  const leftZoneW = hasImg ? imgW + 3 + qrSize : qrSize;
  const textX = leftZoneX + leftZoneW + 4;
  const textW = margin + contentWidth - cardPad - textX;

  const detailLines = buildDetailLines(favorite, item, t, pdfLang);

  doc.setFontSize(8);
  let descLines: string[] = [];
  if (desc) {
    descLines = doc.splitTextToSize(desc, textW);
    if (descLines.length > 3) {
      descLines = [...descLines.slice(0, 2), descLines[2] + '...'];
    }
  }

  const buttons = buildButtons(item, favorite, config, t);

  doc.setFontSize(7);
  let btnRowCount = 0;
  if (buttons.length > 0) {
    let bx = 0;
    btnRowCount = 1;
    for (const btn of buttons) {
      const bw = doc.getTextWidth(btn.label) + 9;
      if (bx + bw > textW && bx > 0) { btnRowCount++; bx = 0; }
      bx += bw + 3;
    }
  }

  doc.setFontSize(12);
  const nameLines = doc.splitTextToSize(item.name, textW);
  const nameLineCount = Math.min(nameLines.length, 2);

  let rightColH = cardPad;
  rightColH += nameLineCount * 5 + 2;
  rightColH += 5;
  rightColH += detailLines.length * 4.5;
  if (descLines.length > 0) rightColH += 2 + descLines.length * 3.5;
  if (btnRowCount > 0) rightColH += 4 + btnRowCount * 7;
  rightColH += cardPad;

  const leftColH = hasImg ? cardPad + imgH + cardPad : cardPad + qrSize + cardPad;
  const cardH = Math.max(leftColH, rightColH);

  checkPage(cardH + 6);

  doc.setFillColor(247, 248, 250);
  doc.roundedRect(margin, yPos, contentWidth, cardH, cardRadius, cardRadius, 'F');

  doc.setDrawColor(230, 232, 237);
  doc.setLineWidth(0.2);
  doc.roundedRect(margin, yPos, contentWidth, cardH, cardRadius, cardRadius, 'S');

  doc.setFillColor(acR, acG, acB);
  doc.roundedRect(margin, yPos + 1.5, accentBarW, cardH - 3, 1.5, 1.5, 'F');

  if (hasImg && imgUrl) {
    await addImageToCard(doc, imgUrl, loadImage, leftZoneX, yPos + cardPad, imgW, imgH);
  }

  await addQrCodeToCard(doc, item, favorite, darkColor, hasImg, leftZoneX, yPos, cardPad, imgW, imgH, qrSize);

  let ty = yPos + cardPad + 5;

  doc.setFontSize(12);
  doc.setTextColor(dkR, dkG, dkB);
  doc.text(nameLines[0], textX, ty);
  if (nameLineCount > 1 && nameLines[1]) {
    ty += 5;
    const truncName = nameLines[1].length > 55 ? nameLines[1].substring(0, 52) + '...' : nameLines[1];
    doc.text(truncName, textX, ty);
  }
  ty += 7;

  doc.setFontSize(9);
  doc.setTextColor(acR, acG, acB);
  const catLabel = t(item.category) + (item.type ? ` · ${t(item.type)}` : '');
  doc.text(catLabel, textX, ty);
  ty += 5;

  doc.setFontSize(8);
  for (const dl of detailLines) {
    if (dl.color === 'accent') doc.setTextColor(acR, acG, acB);
    else if (dl.color === 'medium') doc.setTextColor(mdR, mdG, mdB);
    else doc.setTextColor(100, 100, 110);
    const fitted = doc.splitTextToSize(dl.text, textW);
    doc.text(fitted[0], textX, ty);
    ty += 4.5;
  }

  if (descLines.length > 0) {
    ty += 1;
    doc.setFontSize(8);
    doc.setTextColor(130, 130, 140);
    doc.text(descLines, textX, ty);
    ty += descLines.length * 3.5;
  }

  if (buttons.length > 0) {
    ty += 3;
    drawPillButtons(doc, buttons, textX, textW, ty, acR, acG, acB);
  }

  return cardH;
}

function buildDetailLines(
  favorite: FavoriteItem,
  item: any,
  t: (k: string) => string,
  pdfLang: string
): { text: string; color: 'dark' | 'medium' | 'accent' }[] {
  const lines: { text: string; color: 'dark' | 'medium' | 'accent' }[] = [];

  if (item.fulladress) lines.push({ text: item.fulladress, color: 'dark' });
  if (item.telephone) lines.push({ text: formatPhoneNumber(item.telephone), color: 'dark' });

  if (favorite.sourceType === 'event' || favorite.sourceType === 'event-api') {
    if (item.date) {
      let ds = formatDateSafe(pdfLang, {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
      }, new Date(item.date));
      if (item.hour) {
        ds += ` - ${item.hour}`;
        if (item.end_time) ds += ` > ${item.end_time}`;
        else if (item.duration) ds += ` (${formatTravelTime(item.duration, pdfLang)})`;
      }
      lines.push({ text: ds, color: 'medium' });
    }
    if (item.price) lines.push({ text: `${t('price')}: ${item.price}`, color: 'accent' });
    if (item.place) lines.push({ text: item.place, color: 'dark' });
  } else if (favorite.sourceType === 'standard') {
    if (item.distance) {
      let dt = `${item.distance} km`;
      if (item.timewalk) dt += ` - ${formatTravelTime(item.timewalk, pdfLang)} ${t('walk')}`;
      lines.push({ text: dt, color: 'medium' });
    }
    if (item.price) lines.push({ text: `${t('price')}: ${item.price}`, color: 'accent' });
  } else if (favorite.sourceType === 'liste') {
    if (item.appstore) lines.push({ text: `App Store: ${item.appstore}`, color: 'medium' });
    if (item.playstore) lines.push({ text: `Google Play: ${item.playstore}`, color: 'medium' });
  }

  return lines;
}

function buildButtons(
  item: any,
  favorite: FavoriteItem,
  config: any,
  t: (k: string) => string
): { label: string; url: string }[] {
  const btns: { label: string; url: string }[] = [];

  if (item.booking) btns.push({ label: t('btn_booking'), url: item.booking });
  if (item.url) btns.push({ label: t('btn_more_info'), url: item.url });
  if (item.fulladress) {
    btns.push({
      label: t('btn_itinerary'),
      url: `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(item.fulladress)}`
    });
    if (config.fulladress) {
      btns.push({
        label: t('btn_itinerary_hotel'),
        url: `https://www.google.com/maps/dir/${encodeURIComponent(config.fulladress)}/${encodeURIComponent(item.fulladress)}`
      });
    }
  }
  if (item.telephone) {
    btns.push({ label: t('btn_call'), url: `tel:${item.telephone.replace(/\s+/g, '')}` });
  }

  return btns;
}

async function addImageToCard(
  doc: jsPDF,
  imgUrl: string,
  loadImage: (url: string) => Promise<HTMLImageElement>,
  x: number,
  y: number,
  imgW: number,
  imgH: number
) {
  try {
    const img = await loadImage(imgUrl);
    const ratio = img.width / img.height;
    let w = imgW, h = imgH;
    if (ratio > imgW / imgH) h = imgW / ratio;
    else w = imgH * ratio;
    const ox = (imgW - w) / 2;
    const oy = (imgH - h) / 2;
    doc.addImage(img, 'JPEG', x + ox, y + oy, w, h);
  } catch (error) {
    logger.error('Error adding image to PDF', { category: 'PDF', data: { error, imgUrl } });
    doc.setFillColor(235, 236, 240);
    doc.roundedRect(x, y, imgW, imgH, 2, 2, 'F');
    doc.setFontSize(7);
    doc.setTextColor(180, 180, 185);
    doc.text('Image', x + imgW / 2, y + imgH / 2, { align: 'center' });
  }
}

async function addQrCodeToCard(
  doc: jsPDF,
  item: any,
  favorite: FavoriteItem,
  darkColor: string,
  hasImg: boolean,
  leftZoneX: number,
  yPos: number,
  cardPad: number,
  imgW: number,
  imgH: number,
  qrSize: number
) {
  let qrUrl = '';
  if (item.url) qrUrl = item.url;
  else if (item.booking) qrUrl = item.booking;
  else {
    const base = hotelConfig.meta.url;
    const slug = favorite.metadata.sectionSlug;
    if ((favorite.sourceType === 'event' || favorite.sourceType === 'event-api') && item.date && item.hour) {
      qrUrl = `${base}/${slug}/${slugify(item.name)}-${item.date.replace(/-/g, '')}-${item.hour.replace(':', '')}`;
    } else {
      qrUrl = `${base}/${slug}/${slugify(item.name)}`;
    }
  }

  const qrX = hasImg ? leftZoneX + imgW + 3 : leftZoneX;
  const qrY = hasImg
    ? yPos + cardPad + (imgH - qrSize) / 2
    : yPos + cardPad;

  try {
    const qrDataUrl = await QRCode.toDataURL(qrUrl, {
      width: 80,
      margin: 1,
      color: { dark: darkColor, light: '#F7F8FA' }
    });
    doc.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
  } catch (error) {
    logger.error('Error generating QR code', { category: 'PDF', data: { error, url: qrUrl } });
  }
}

function drawPillButtons(
  doc: jsPDF,
  buttons: { label: string; url: string }[],
  startX: number,
  maxW: number,
  ty: number,
  acR: number, acG: number, acB: number
) {
  doc.setFontSize(7);
  let bx = startX;

  for (const btn of buttons) {
    const pillW = doc.getTextWidth(btn.label) + 9;
    const pillH = 5.5;

    if (bx + pillW > startX + maxW && bx > startX) {
      bx = startX;
      ty += 7;
    }

    doc.setFillColor(acR, acG, acB);
    doc.roundedRect(bx, ty - 4, pillW, pillH, 2.5, 2.5, 'F');
    doc.setTextColor(255, 255, 255);
    doc.text(btn.label, bx + pillW / 2, ty - 0.8, { align: 'center' });
    doc.link(bx, ty - 4, pillW, pillH, { url: btn.url });
    bx += pillW + 3;
  }
}
